import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Link,
  Alert,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import MinerRegistrationForm from '../components/registration/MinerRegistrationForm';
import ManufacturerRegistrationForm from '../components/registration/ManufacturerRegistrationForm';
import TraderRegistrationForm from '../components/registration/TraderRegistrationForm';
import RegulatorRegistrationForm from '../components/registration/RegulatorRegistrationForm';

/**
 * Registration page component
 */
const RegisterPage = () => {
  const { register, loading, error } = useAuth();
  const [stakeholderType, setStakeholderType] = useState('miner');
  const [success, setSuccess] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState('');

  const handleTabChange = (event, newValue) => {
    setStakeholderType(newValue);
  };

  const handleTermsChange = (event) => {
    setTermsAccepted(event.target.checked);
    if (event.target.checked) {
      setTermsError('');
    }
  };

  const handleSubmit = async (formData) => {
    if (!termsAccepted) {
      setTermsError('You must accept the terms and conditions');
      return;
    }

    try {
      await register(formData);
      setSuccess(true);

      // Show success message and redirect to home page
      setTimeout(() => {
        window.location.href = '/';
      }, 5000);
    } catch (err) {
      // Error is handled in AuthContext
      console.error('Registration error:', err);
    }
  };

  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          marginTop: 8,
          marginBottom: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            Register Interest in Laprecio Minerals Trading Platform
          </Typography>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body1">
              <strong>Platform Status:</strong> The Laprecio Minerals Trading Platform is currently in development. By registering your interest now, you'll be among the first to know when the platform launches.
            </Typography>
          </Alert>

          <Typography variant="body1" align="center" paragraph sx={{ mb: 4 }}>
            Select your role in the minerals supply chain to register your interest
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 4 }}>
              {error}
              <Typography variant="body2" sx={{ mt: 1 }}>
                If you continue to experience issues, please contact us at <Link href="mailto:hello@laprecio.com">hello@laprecio.com</Link>
              </Typography>
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 4 }}>
              Thank you for registering your interest! We'll contact you when the platform launches. You'll be redirected to the home page in a few seconds.
            </Alert>
          )}

          <Tabs
            value={stakeholderType}
            onChange={handleTabChange}
            variant="fullWidth"
            indicatorColor="primary"
            textColor="primary"
            sx={{ mb: 4 }}
          >
            <Tab label="Miner / Producer" value="miner" />
            <Tab label="Manufacturer / Buyer" value="manufacturer" />
            <Tab label="Trader / Broker" value="trader" />
            <Tab label="Regulator / Auditor" value="regulator" />
          </Tabs>

          <Box sx={{ mt: 2 }}>
            {stakeholderType === 'miner' && (
              <MinerRegistrationForm onSubmit={handleSubmit} loading={loading} />
            )}
            {stakeholderType === 'manufacturer' && (
              <ManufacturerRegistrationForm onSubmit={handleSubmit} loading={loading} />
            )}
            {stakeholderType === 'trader' && (
              <TraderRegistrationForm onSubmit={handleSubmit} loading={loading} />
            )}
            {stakeholderType === 'regulator' && (
              <RegulatorRegistrationForm onSubmit={handleSubmit} loading={loading} />
            )}

            <Box sx={{ mt: 2, mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="termsAccepted"
                    color="primary"
                    checked={termsAccepted}
                    onChange={handleTermsChange}
                  />
                }
                label={
                  <span>
                    I agree to the{' '}
                    <Link component={RouterLink} to="/terms">
                      terms and conditions
                    </Link>
                  </span>
                }
              />
              {termsError && (
                <Typography color="error" variant="caption" display="block">
                  {termsError}
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Link component={RouterLink} to="/" variant="body2">
            Back to Home
          </Link>
        </Box>
      </Box>
    </Container>
  );
};

export default RegisterPage;
